import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function SurgeProtectionPage() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Surge Protection Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Protect your electronics from power surges in Watford. Surge protection device installation for homes & businesses. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Protect your electronics from power surges in Watford. Surge protection device installation for homes & businesses. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    // Add LocalBusiness JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/safety-compliance/surge-protection",
      "telephone": "+442031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      },
      "serviceType": "Surge Protection",
      "priceRange": "££",
      "openingHours": "Mo-Su 00:00-23:59"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const areasServed = [
    'Watford Town Centre',
    'Oxhey',
    'Bushey',
    'Croxley Green',
    'Abbots Langley',
    'Rickmansworth',
    'Garston',
    'North Watford',
    'West Watford',
    'Carpenders Park',
    'Leavesden',
    'Nascot Wood',
    'Holywell',
    'Meriden',
    'Cassiobury'
  ];

  const whatsIncluded = [
    {
      icon: '⚡',
      title: 'Type 2 SPD Installation',
      description: 'Consumer unit surge protection devices to BS 7671:2018 Amendment 2 standards'
    },
    {
      icon: '🏠',
      title: 'Whole-House Protection',
      description: 'Protect all circuits and appliances from voltage spikes and surges'
    },
    {
      icon: '🌩️',
      title: 'Lightning Protection',
      description: 'Safeguard against lightning strikes and power grid fluctuations'
    },
    {
      icon: '💻',
      title: 'Sensitive Equipment',
      description: 'Extra protection for computers, TVs, and expensive electronics'
    },
    {
      icon: '✅',
      title: 'Compliance Certification',
      description: 'Meet 18th Edition wiring regulations for new installations and upgrades'
    },
    {
      icon: '🔧',
      title: 'Professional Installation',
      description: 'Expert fitting by NICEIC approved electricians with full testing'
    }
  ];

  const benefits = [
    {
      icon: '💰',
      title: 'Equipment Protection',
      description: 'Prevent costly damage to appliances and electronics from power surges'
    },
    {
      icon: '🔥',
      title: 'Fire Prevention',
      description: 'Reduce risk of electrical fires caused by voltage spikes'
    },
    {
      icon: '⚖️',
      title: 'Regulation Compliant',
      description: 'Required for new builds and major electrical works since 2022'
    },
    {
      icon: '🏡',
      title: 'Property Value',
      description: 'Modern electrical protection adds value to your home'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://public.readdy.ai/ai/img_res/677bd4e3effa4dace88a2f60167a1c50.jpg" 
            alt="Surge Protection Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">🏆 NICEIC Approved</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">📍 Watford</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Surge Protection<br />
            <span className="text-[#D4AF37]">in Watford</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            Professional surge protection device (SPD) installation for homes and businesses. Protect your valuable electronics from power surges, lightning strikes, and voltage spikes.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Complete Surge Protection Service</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional SPD installation to protect your property from electrical surges
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6 text-3xl">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why Install Surge Protection?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential protection for modern homes filled with expensive electronics
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-full mb-6 text-4xl mx-auto shadow-lg">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watford's trusted experts for surge protection installation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-shield-check-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">Certified SPD installation</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Quick Installation</h3>
              <p className="text-gray-300 text-sm">Minimal disruption to your property</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-file-list-3-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">18th Edition Compliant</h3>
              <p className="text-gray-300 text-sm">Meet latest wiring regulations</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Expert Advice</h3>
              <p className="text-gray-300 text-sm">Best protection for your property</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 flex items-center justify-center bg-[#D4AF37] rounded-full text-4xl flex-shrink-0">
                🏆
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Surge Protection Specialists</h3>
                <p className="text-gray-300 leading-relaxed">
                  Our NICEIC approved electricians are experts in surge protection installation. We'll assess your property and install the right SPD to protect your valuable electronics and appliances from power surges.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">
              Surge protection installation across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Surge Protection?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Protect your valuable electronics and appliances with professional SPD installation from NICEIC approved electricians in Watford.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Request Callback
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Quick Installation</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>18th Edition Compliant</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}